<!--

	Makes a VueList searchable.

-->
<template>
	<div>
		<slot name="search-form" :store="storeInstance" />
		<div class="vl-parent">
			<loading :active="isResetting" :is-full-page="false" delayed />
			<slot :is-empty="isEmpty" />
		</div>
	</div>
</template>
<script lang="ts">
import debounce, { type DebouncedFunc } from "lodash-es/debounce";
import { Component, Prop, Vue, Watch, toNative } from "vue-facing-decorator";
import { useListStore, type IListStore } from "../../Stores/VueList/ListStore";
import { Loading } from "@/chunks/LoggedIn";

@Component({
	components: { Loading },
})
export class SearchableList<S> extends Vue {
	/**
	 * The VueListStore backing this list.
	 */
	@Prop({ required: true, type: String }) protected store!: string;
	/**
	 * The URL or array from which the VueListStore should load its items.
	 */
	@Prop({ required: true }) protected data!: string | S[];
	/**
	 * How long we wait before we notify the store of changes; 250ms
	 * is the reaction time of most people (https://stackoverflow.com/a/44755058/812379)
	 * so as a default we wait that long before we show a spinner and send the data off.
	 */
	@Prop({ required: false, default: 250, type: Number }) protected debounceMilliseconds!: number;

	private debouncedReset = null as unknown as DebouncedFunc<() => Promise<void>>;

	private pendingResetPromise?: {
		resolve: () => void
		reject: () => void
	} = undefined;

	protected created() {
		this.debouncedReset = debounce(this.reset, this.debounceMilliseconds);
	}

	/**
	 * Tell the store to invalidate its content and load from data in the future.
	 * Return a promise that resolves once any new data has been loaded or if another reset has made it obsolete.
	 */
	protected reset(): Promise<void> {
		if (this.pendingResetPromise != null) {
			this.pendingResetPromise.resolve();
		}

		void this.storeInstance.reset(this.data);
		return new Promise((resolve, reject) => {
			this.pendingResetPromise = { resolve, reject };
		});
	}

	@Watch("data")
	protected onUrlChanged(): void {
		void this.debouncedReset();
	}

	get isEmpty(): boolean {
		return !this.isResetting && this.storeInstance.items.length === 0;
	}

	get storeInstance(): IListStore<S> {
		return useListStore<S>(this.store);
	}

	get isResetting(): boolean {
		const ret = this.storeInstance.isResetting;
		if (!ret && this.pendingResetPromise != null) {
			this.pendingResetPromise.resolve();
		}
		return Boolean(ret);
	}
}

export default toNative(SearchableList);
</script>
